<template>
  <div v-if="!widgetHidden" class="cw">
    <button class="cw-btn" type="button" @click="toggleOpen" aria-label="Warenkorb öffnen">
      <svg xmlns="http://www.w3.org/2000/svg" width="21.36" height="27.153" viewBox="0 0 21.36 27.153">
        <g id="icon-cart" transform="translate(-641 -309.997)">
          <path id="Pfad_9" data-name="Pfad 9" d="M10.89,10.93a2.172,2.172,0,0,1-.82-.86,4.206,4.206,0,0,1-.2-.86H7.55L6.2,27.33l14.65,1.38.21-19.46-4.52-.02v.69a.8.8,0,0,1,.3-.06.978.978,0,0,1,.9,1.04.978.978,0,0,1-.9,1.04.939.939,0,0,1-.86-.73,1.331,1.331,0,0,1-.45-.98,8.115,8.115,0,0,1-.02-1.01L10.89,9.2a2.449,2.449,0,0,0,.12.5.652.652,0,0,0,.26.27.8.8,0,0,1,.5-.17.979.979,0,0,1,.9,1.04.972.972,0,0,1-.9,1.04.932.932,0,0,1-.89-.95m-.04-2.54h2.75a11.548,11.548,0,0,1,.78-4.28h0a1.913,1.913,0,0,1,.53.9,12.987,12.987,0,0,0-.5,3.4h1.14c.01-.53.03-1.11,0-1.68-.06-1.7-.42-3.33-2.07-3.28a1.644,1.644,0,0,0-.6.12,3.065,3.065,0,0,0-1.59,1.95,8.628,8.628,0,0,0-.45,2.87M7.17,8.38H9.82a9.669,9.669,0,0,1,.51-3.2,4,4,0,0,1,2.19-2.57,2.747,2.747,0,0,1,.93-.18c2.51-.09,3.05,2.04,3.12,4.27.02.58,0,1.17,0,1.72h2.75c.02-.18.03-.37.04-.58a7.614,7.614,0,0,0-.34-2.64,2.683,2.683,0,0,0-1.29-1.69,1.949,1.949,0,0,0-.71-.16,2.59,2.59,0,0,0-.76.1,3.278,3.278,0,0,0-.48-.63.435.435,0,0,0-.07-.06,3.018,3.018,0,0,1,1.33-.23,2.728,2.728,0,0,1,1.02.23,3.482,3.482,0,0,1,1.73,2.17,8.571,8.571,0,0,1,.38,2.92,5.088,5.088,0,0,1-.04.56h1.38l.38.04,1.02.11.3,17.42-1.39,2.15.16.12,1.49-1.95,2.16,1.11.1-.18-2.11-1.32-.3-17.42,1.4-.41.49-.14.04.51,1.41,19.34.02.34-.33.09-4.68,1.22-.37.1-.45-.04L5.73,28.07l-.4-.04.03-.4L6.77,8.72l.03-.38h.38Z" transform="translate(635.68 307.57)" fill="#565655"/>
          <rect id="Rechteck_7" data-name="Rechteck 7" width="21.35" height="27.15" transform="translate(641 310)" fill="none"/>
        </g>
      </svg>

      <span class="cw-label">Warenkorb</span>

      <span v-if="cartCount > 0" class="cw-badge" :class="{ pulse: pulse }" aria-label="Artikel im Warenkorb">
        {{ cartCount }}
      </span>
    </button>

    <CartDrawer
      v-model:open="open"
      :autoLoad="true"
      variant="drawer"
      :groups-open-by-default="false"
      :checkout-href="cartUrl"
      @checkout="goToCart"
    />

    <Teleport to="body">
      <div v-if="showMergeDialog" class="cw-merge" role="dialog" aria-modal="true" aria-labelledby="cw-merge-title">
        <div class="cw-merge__backdrop" @click="mergeBusy || dismissMergeDialog()"></div>
        <div class="cw-merge__box">
          <h2 id="cw-merge-title" class="cw-merge__title">Früherer Warenkorb gefunden</h2>
          <p class="cw-merge__text">
            Es befinden sich noch
            <strong>{{ mergeItemCountLabel }}</strong>
            aus einem früheren Warenkorb<template v-if="mergeUploadDateLabel"> (Upload vom {{ mergeUploadDateLabel }})</template>
            in Ihrem Konto. Möchten Sie diese zusätzlich übernehmen oder löschen?
          </p>
          <div class="cw-merge__actions">
            <button type="button" class="cw-merge__btn cw-merge__btn--primary" :disabled="mergeBusy" @click="onAdoptMerge">
              Zusätzlich übernehmen
            </button>
            <button type="button" class="cw-merge__btn cw-merge__btn--ghost" :disabled="mergeBusy" @click="onDiscardMerge">
              Löschen
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import CartDrawer from './components/CartDrawer.vue'
import { useCartStore, onCartChanged } from './store/cart.ts'
import { getWidgetConfig } from './runtime/widgetConfig'

const emit = defineEmits<{ (e: 'checkout'): void }>()
const cart = useCartStore()
const cartUrl = computed(() => String((getWidgetConfig() as any)?.cartUrl || '/checkout/cart').trim())

const open = ref(false)
const pulse = ref(false)

// "Adopt or discard a previous, unfinished cart" dialog (shown once after login).
const showMergeDialog = ref(false)
const mergeBusy = ref(false)
const mergeItemCountLabel = computed(() => {
  const n = Number(cart.pendingMerge?.itemCount || 0)
  return n === 1 ? '1 Artikel' : `${n} Artikel`
})
const mergeUploadDateLabel = computed(() => {
  const raw = cart.pendingMerge?.uploadDate
  if (!raw) return ''
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
})

async function checkPendingMerge() {
  // A server-rendered page (e.g. the Twig cart page) may already own this prompt.
  if ((window as any).__PFE_PENDING_MERGE_HANDLED === true) return
  try {
    const res = await cart.fetchPendingMerge()
    showMergeDialog.value = !!res?.hasPending
  } catch {
    showMergeDialog.value = false
  }
}

function dismissMergeDialog() {
  showMergeDialog.value = false
}

async function onAdoptMerge() {
  if (mergeBusy.value) return
  mergeBusy.value = true
  try {
    await cart.resolvePendingMerge('adopt')
    showMergeDialog.value = false
    open.value = true
  } catch {} finally {
    mergeBusy.value = false
  }
}

async function onDiscardMerge() {
  if (mergeBusy.value) return
  mergeBusy.value = true
  try {
    await cart.resolvePendingMerge('discard')
    showMergeDialog.value = false
  } catch {} finally {
    mergeBusy.value = false
  }
}
let pulseTimer: ReturnType<typeof setTimeout> | null = null
const BODY_OPEN_CLASS = 'pfe-cart-widget-open'
const widgetConfig = computed(() => getWidgetConfig() as any)
const widgetHidden = computed(() => {
  const cfg = widgetConfig.value || {}
  if (cfg?.forceHide === true) return true
  if (cfg?.hideWhenEmpty === true && cartCount.value <= 0) return true
  return false
})

function toggleOpen() {
  open.value = !open.value
}

function syncBodyOpenClass(isOpen: boolean) {
  if (typeof document === 'undefined') return
  document.body.classList.toggle(BODY_OPEN_CLASS, isOpen)
}

function goToCart() {
  if (cartUrl.value) {
    let navigated = false
    const commit = () => {
      if (navigated) return
      navigated = true
      window.location.href = cartUrl.value
    }
    window.setTimeout(commit, 180)
    void cart.primeCheckoutSession().catch(() => {}).finally(commit)
    return
  }
  emit('checkout')
}

const cartCount = computed(() => cart.count)
const CART_STATE_UPDATED_EVENT = 'pfe:cart-state-updated'

// Debounced refresh to collapse bursts of signals (e.g. multiple add/remove in short time)
let t: any = null
let suppressServerRefreshUntil = 0
const requestRefresh = () => {
  if (Date.now() < suppressServerRefreshUntil) return
  if (widgetConfig.value?.clearCartOnMount === true) return
  if (t) clearTimeout(t)
  t = setTimeout(async () => {
    t = null
    try { await cart.refreshCartIfSessionExists() } catch {}
  }, 120)
}

function onExternalCartStateUpdated(ev: Event) {
  const detail = (ev as CustomEvent<any>)?.detail || null
  if (!detail || typeof detail !== 'object') return
  const items = Array.isArray(detail.items) ? detail.items : null
  if (!items) return
  cart.$patch({
    cart: {
      ...(cart.cart || { id: 'local', items: [] as any[] }),
      ...detail,
      items,
    } as any,
  })
  if (!items.length) {
    suppressServerRefreshUntil = Date.now() + 5000
    cart.clearToken(false)
    cart.clearCartLocal(false)
  }
}

let off: null | (() => void) = null
onMounted(async () => {
  if (widgetConfig.value?.clearCartOnMount === true) {
    try { localStorage.removeItem('cart_token') } catch {}
    suppressServerRefreshUntil = Date.now() + 5000
    cart.clearToken(false)
    cart.clearCartLocal(false)
  }
  // initial
  if (widgetConfig.value?.clearCartOnMount !== true) {
    try { await cart.refreshCartIfSessionExists() } catch {}
    // After login a previous, unfinished cart may be waiting for an adopt/discard decision.
    try { await checkPendingMerge() } catch {}
  }
  syncBodyOpenClass(open.value)
  window.addEventListener(CART_STATE_UPDATED_EVENT, onExternalCartStateUpdated as EventListener)

  off = onCartChanged((_p) => {
    // Count updates even if drawer is closed.
    requestRefresh()
    pulse.value = true
    if (pulseTimer) clearTimeout(pulseTimer)
    pulseTimer = setTimeout(() => {
      pulse.value = false
      pulseTimer = null
    }, 650)
  })
})

watch(open, (value) => {
  syncBodyOpenClass(value)
})

watch(widgetHidden, (hidden) => {
  if (!hidden) return
  open.value = false
  pulse.value = false
  syncBodyOpenClass(false)
})

onBeforeUnmount(() => {
  try { off?.() } catch {}
  if (t) clearTimeout(t)
  if (pulseTimer) clearTimeout(pulseTimer)
  window.removeEventListener(CART_STATE_UPDATED_EVENT, onExternalCartStateUpdated as EventListener)
  syncBodyOpenClass(false)
})
</script>

<style scoped lang="scss">
.cw{
  display:inline-flex;
  align-items:center;
  position:relative;
  overflow:visible;
}
.cw-btn{
  min-height: 38px;
  border: 0;
  background: #fff;
  padding: 0 12px;
  display:inline-flex;
  align-items:center;
  gap: 8px;
  cursor:pointer;
  font-size: 1.5rem;
  font-weight: 500;
  border-radius: 4px;
  position: relative;
  box-shadow: none;
}
.cw-btn:hover{
  color: #d2b879;
}
.cw-label{ }
.cw-badge{
  min-width: 20px;
  height: 20px;
  padding: 0 7px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  background: rgba(239,68,68,1);
  border-radius: 999px;
  margin-left: 4px;
}
.cw-badge.pulse{ animation: pulse .65s ease-out; }
@keyframes pulse {
  0%   { transform: scale(1); }
  35%  { transform: scale(1.18); }
  100% { transform: scale(1); }
}

:global(body.pfe-cart-widget-open #tsbadge),
:global(body.pfe-cart-widget-open .ts-trustbadge),
:global(body.pfe-cart-widget-open [class*="trustbadge"]) {
  opacity: 0 !important;
  pointer-events: none !important;
}

.cw-merge{
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.cw-merge__backdrop{
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,.45);
}
.cw-merge__box{
  position: relative;
  background: #fff;
  max-width: 440px;
  width: 100%;
  border-radius: 10px;
  padding: 24px;
  box-shadow: 0 20px 60px rgba(0,0,0,.25);
}
.cw-merge__title{ margin: 0 0 10px; font-size: 1.4rem; }
.cw-merge__text{ margin: 0 0 18px; line-height: 1.5; }
.cw-merge__actions{ display: flex; gap: 10px; flex-wrap: wrap; }
.cw-merge__btn{
  flex: 1 1 auto;
  min-height: 40px;
  padding: 0 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  border: 1px solid transparent;
}
.cw-merge__btn--primary{ background: #d2b879; color: #fff; }
.cw-merge__btn--ghost{ background: #fff; border-color: #cbd5e1; color: #334155; }
.cw-merge__btn:disabled{ opacity: .6; pointer-events: none; }
</style>
